'use client';

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ENROLLMENT_NETWORKS,
  NETWORK_LABELS,
  merchantBasicsSchema,
  midDetailsSchema,
  type EnrollmentNetwork,
  type MerchantBasics,
  type MidDetails,
  type RdrRuleInput,
} from '@/lib/enroll/schema';

/**
 * Phase 2-G4 Track C Form 2 — public self-serve enrollment wizard.
 *
 * Steps: account → merchant basics → MID details → service selection → review
 * → submit. Each step validates client-side (shared Zod schema for merchant/MID;
 * validateAccount for the account step). The five core fields per merchant/MID
 * step are required; the fuller SOP onboarding-sheet fields (C10) are optional
 * and never block submission. Every submission now carries account credentials
 * (Task 9) — POSTs the whole submission to the public BFF, then does a full
 * navigation to the returned dashboardPath (or the status page keyed by token)
 * so the freshly-set session cookie applies.
 */

type Step = 0 | 1 | 2 | 3 | 4;
const STEP_LABELS = ['Account', 'Merchant', 'MID details', 'Services', 'Review'];

interface AccountInput {
  email: string;
  password: string;
  confirm: string;
}
const EMPTY_ACCOUNT: AccountInput = { email: '', password: '', confirm: '' };

/** Extra optional merchant fields, held as strings for controlled inputs. */
type MerchantExtra = {
  sellerProductBrand: string;
  sellerProductDescription: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  supportPhone: string;
  supportEmail: string;
  refundPolicyUrl: string;
  termsUrl: string;
};

/** Extra optional MID fields, held as strings for controlled inputs. */
type MidExtra = {
  descriptorVariations: string;
  bankMid: string;
  bankTid: string;
  alertMinimumAmount: string;
  alertMaximumAmount: string;
  ethocaAmountThreshold: string;
};

const EMPTY_MERCHANT: MerchantBasics = {
  sellerLegalName: '',
  merchantNameOnWebsite: '',
  websiteUrl: '',
  contactPhone: '',
  contactEmail: '',
};
const EMPTY_MID: MidDetails = {
  midNumber: '',
  mcc: '',
  acquirerBin: '',
  caid: '',
  descriptor: '',
};
const EMPTY_MERCHANT_EXTRA: MerchantExtra = {
  sellerProductBrand: '',
  sellerProductDescription: '',
  street: '',
  city: '',
  region: '',
  postalCode: '',
  countryCode: '',
  supportPhone: '',
  supportEmail: '',
  refundPolicyUrl: '',
  termsUrl: '',
};
const EMPTY_MID_EXTRA: MidExtra = {
  descriptorVariations: '',
  bankMid: '',
  bankTid: '',
  alertMinimumAmount: '',
  alertMaximumAmount: '',
  ethocaAmountThreshold: '',
};
const DEFAULT_RDR_RULE: RdrRuleInput = {
  name: 'Refund All',
  attribute: 'Transaction Amount',
  operator: 'Greater Than or Equals',
  value: '0.01',
};

function validateAccount(a: AccountInput): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email)) errs['account.email'] = 'Enter a valid email address.';
  if (a.password.length < 12) errs['account.password'] = 'Password must be at least 12 characters.';
  if (a.confirm !== a.password) errs['account.confirm'] = 'Passwords do not match.';
  return errs;
}

/** Mirrors rdrRuleInputSchema's non-empty rules — the only client-side gate on
 * RDR rule contents now that the final-submit schema parse is gone. */
function validateRdrRule(r: RdrRuleInput): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!r.name.trim()) errs['rdr.name'] = 'Required';
  if (!r.attribute.trim()) errs['rdr.attribute'] = 'Required';
  if (!r.operator.trim()) errs['rdr.operator'] = 'Required';
  if (!r.value.trim()) errs['rdr.value'] = 'Required';
  return errs;
}

function fieldErrors(issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of issues) {
    const key = String(i.path[i.path.length - 1]);
    if (!(key in out)) out[key] = i.message;
  }
  return out;
}

/** Keep only non-empty (trimmed) values — optional fields reject ''. */
function trimmedStrings<K extends string>(obj: Record<K, string>): Partial<Record<K, string>> {
  const out: Partial<Record<K, string>> = {};
  (Object.keys(obj) as K[]).forEach((k) => {
    const v = obj[k].trim();
    if (v) out[k] = v;
  });
  return out;
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function buildMerchantPayload(merchant: MerchantBasics, extra: MerchantExtra): MerchantBasics {
  return { ...merchant, ...trimmedStrings(extra) };
}

function buildMidPayload(mid: MidDetails, extra: MidExtra): MidDetails {
  return {
    ...mid,
    ...trimmedStrings({ bankMid: extra.bankMid, bankTid: extra.bankTid }),
    ...(extra.descriptorVariations.trim()
      ? { descriptorVariations: splitLines(extra.descriptorVariations) }
      : {}),
    ...(extra.alertMinimumAmount.trim() ? { alertMinimumAmount: Number(extra.alertMinimumAmount) } : {}),
    ...(extra.alertMaximumAmount.trim() ? { alertMaximumAmount: Number(extra.alertMaximumAmount) } : {}),
    ...(extra.ethocaAmountThreshold.trim()
      ? { ethocaAmountThreshold: Number(extra.ethocaAmountThreshold) }
      : {}),
  };
}

export interface EnrollWizardProps {
  /** White-label attribution — filed under this reseller when present. */
  resellerSlug?: string;
  /** Brand name for copy ("Create your <brand> account"). */
  brandName?: string;
}

export function EnrollWizard({ resellerSlug, brandName }: EnrollWizardProps = {}) {
  const [step, setStep] = useState<Step>(0);
  const [account, setAccount] = useState<AccountInput>(EMPTY_ACCOUNT);
  const [merchant, setMerchant] = useState<MerchantBasics>(EMPTY_MERCHANT);
  const [merchantExtra, setMerchantExtra] = useState<MerchantExtra>(EMPTY_MERCHANT_EXTRA);
  const [mid, setMid] = useState<MidDetails>(EMPTY_MID);
  const [midExtra, setMidExtra] = useState<MidExtra>(EMPTY_MID_EXTRA);
  const [networks, setNetworks] = useState<Set<EnrollmentNetwork>>(new Set());
  const [rdrRule, setRdrRule] = useState<RdrRuleInput>(DEFAULT_RDR_RULE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedNetworks = useMemo(() => [...networks], [networks]);

  const merchantReviewRows = useMemo<[string, string][]>(
    () =>
      Object.entries(buildMerchantPayload(merchant, merchantExtra)).map(
        ([k, v]) => [k, v ?? ''] as [string, string],
      ),
    [merchant, merchantExtra],
  );

  const midReviewRows = useMemo<[string, string][]>(() => {
    const payload = buildMidPayload(mid, midExtra);
    return Object.entries(payload).map(([k, v]) => [
      k,
      Array.isArray(v) ? v.join(', ') : String(v),
    ]);
  }, [mid, midExtra]);

  const next = useCallback(() => {
    setErrors({});
    if (step === 0) {
      const errs = validateAccount(account);
      if (Object.keys(errs).length > 0) return setErrors(errs);
    } else if (step === 1) {
      const r = merchantBasicsSchema.safeParse(buildMerchantPayload(merchant, merchantExtra));
      if (!r.success) return setErrors(fieldErrors(r.error.issues));
    } else if (step === 2) {
      const r = midDetailsSchema.safeParse(buildMidPayload(mid, midExtra));
      if (!r.success) return setErrors(fieldErrors(r.error.issues));
    } else if (step === 3) {
      if (selectedNetworks.length === 0) {
        return setErrors({ networks: 'Select at least one service' });
      }
      if (networks.has('rdr')) {
        const errs = validateRdrRule(rdrRule);
        if (Object.keys(errs).length > 0) return setErrors(errs);
      }
    }
    setStep((s) => Math.min(4, s + 1) as Step);
  }, [step, account, merchant, merchantExtra, mid, midExtra, selectedNetworks, networks, rdrRule]);

  const back = useCallback(() => {
    setErrors({});
    setStep((s) => Math.max(0, s - 1) as Step);
  }, []);

  const toggleNetwork = useCallback((n: EnrollmentNetwork) => {
    setNetworks((prev) => {
      const copy = new Set(prev);
      if (copy.has(n)) copy.delete(n);
      else copy.add(n);
      return copy;
    });
  }, []);

  const submit = useCallback(async () => {
    setSubmitError(null);
    const payload = {
      merchant: buildMerchantPayload(merchant, merchantExtra),
      mid: buildMidPayload(mid, midExtra),
      networks: selectedNetworks,
      ...(networks.has('rdr') ? { rdrRule } : {}),
      account: { email: account.email, password: account.password },
      ...(resellerSlug ? { resellerSlug } : {}),
    };
    setSubmitting(true);
    try {
      const res = await fetch('/api/public/enroll', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) {
        setSubmitError('Too many submissions. Please wait a moment and try again.');
        return;
      }
      const body = (await res.json()) as {
        data?: { token: string; dashboardPath?: string | null };
        error?: { code?: string; message?: string };
      };
      if (res.status === 409 && body.error?.code === 'email_in_use') {
        setSubmitError('An account with this email already exists — log in instead.');
        setStep(0);
        return;
      }
      if (!res.ok || !body.data) {
        setSubmitError(body.error?.message ?? 'Submission failed. Please check your entries and try again.');
        return;
      }
      // Full navigation (not router.push) so the freshly-set session cookie
      // applies to the destination render.
      window.location.assign(
        body.data.dashboardPath ?? `/enroll/status/${encodeURIComponent(body.data.token)}`,
      );
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [merchant, merchantExtra, mid, midExtra, selectedNetworks, networks, rdrRule, account, resellerSlug]);

  return (
    <div className="space-y-8" data-testid="enroll-wizard">
      <Stepper current={step} />

      {step === 0 ? (
        <section className="space-y-4" data-testid="step-account">
          <div>
            <h2 className="text-lg font-semibold">
              Create your {brandName ?? 'Fraud Defender'} account
            </h2>
            <p className="mt-1 text-sm text-fg-muted">
              You&apos;ll use this to track your enrollment and manage alerts once you&apos;re live.
            </p>
          </div>
          <Field id="accountEmail" label="Email address" error={errors['account.email']}>
            <Input
              id="accountEmail"
              type="email"
              value={account.email}
              onChange={(e) => setAccount({ ...account, email: e.target.value })}
            />
          </Field>
          <Field
            id="accountPassword"
            label="Password (min. 12 characters)"
            error={errors['account.password']}
          >
            <Input
              id="accountPassword"
              type="password"
              value={account.password}
              onChange={(e) => setAccount({ ...account, password: e.target.value })}
            />
          </Field>
          <Field id="accountConfirm" label="Confirm password" error={errors['account.confirm']}>
            <Input
              id="accountConfirm"
              type="password"
              value={account.confirm}
              onChange={(e) => setAccount({ ...account, confirm: e.target.value })}
            />
          </Field>
          <p className="text-xs text-fg-muted">
            Already have an account?{' '}
            <a className="underline" href="/login">
              Log in
            </a>{' '}
            to add a new MID instead.
          </p>
        </section>
      ) : null}

      {step === 1 ? (
        <fieldset className="space-y-4" data-testid="step-merchant">
          <legend className="text-base font-semibold">Merchant details</legend>
          <Field id="sellerLegalName" label="Seller legal name" error={errors.sellerLegalName}>
            <Input
              id="sellerLegalName"
              value={merchant.sellerLegalName}
              onChange={(e) => setMerchant({ ...merchant, sellerLegalName: e.target.value })}
            />
          </Field>
          <Field id="merchantNameOnWebsite" label="Merchant name on website" error={errors.merchantNameOnWebsite}>
            <Input
              id="merchantNameOnWebsite"
              value={merchant.merchantNameOnWebsite}
              onChange={(e) => setMerchant({ ...merchant, merchantNameOnWebsite: e.target.value })}
            />
          </Field>
          <Field id="websiteUrl" label="Website URL" error={errors.websiteUrl}>
            <Input
              id="websiteUrl"
              type="url"
              placeholder="https://"
              value={merchant.websiteUrl}
              onChange={(e) => setMerchant({ ...merchant, websiteUrl: e.target.value })}
            />
          </Field>
          <Field id="contactPhone" label="Contact phone" error={errors.contactPhone}>
            <Input
              id="contactPhone"
              value={merchant.contactPhone}
              onChange={(e) => setMerchant({ ...merchant, contactPhone: e.target.value })}
            />
          </Field>
          <Field id="contactEmail" label="Contact email" error={errors.contactEmail}>
            <Input
              id="contactEmail"
              type="email"
              value={merchant.contactEmail}
              onChange={(e) => setMerchant({ ...merchant, contactEmail: e.target.value })}
            />
          </Field>

          <div className="space-y-4 border-t pt-4">
            <p className="text-sm font-medium text-fg-muted">Additional details (optional)</p>
            <Field id="sellerProductBrand" label="Product / brand name" error={errors.sellerProductBrand}>
              <Input
                id="sellerProductBrand"
                value={merchantExtra.sellerProductBrand}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, sellerProductBrand: e.target.value })}
              />
            </Field>
            <Field
              id="sellerProductDescription"
              label="Product description"
              error={errors.sellerProductDescription}
            >
              <Input
                id="sellerProductDescription"
                value={merchantExtra.sellerProductDescription}
                onChange={(e) =>
                  setMerchantExtra({ ...merchantExtra, sellerProductDescription: e.target.value })
                }
              />
            </Field>
            <Field id="street" label="Street address" error={errors.street}>
              <Input
                id="street"
                value={merchantExtra.street}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, street: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field id="city" label="City" error={errors.city}>
                <Input
                  id="city"
                  value={merchantExtra.city}
                  onChange={(e) => setMerchantExtra({ ...merchantExtra, city: e.target.value })}
                />
              </Field>
              <Field id="region" label="State / region" error={errors.region}>
                <Input
                  id="region"
                  value={merchantExtra.region}
                  onChange={(e) => setMerchantExtra({ ...merchantExtra, region: e.target.value })}
                />
              </Field>
              <Field id="postalCode" label="Postal code" error={errors.postalCode}>
                <Input
                  id="postalCode"
                  value={merchantExtra.postalCode}
                  onChange={(e) => setMerchantExtra({ ...merchantExtra, postalCode: e.target.value })}
                />
              </Field>
              <Field id="countryCode" label="Country (2-letter ISO)" error={errors.countryCode}>
                <Input
                  id="countryCode"
                  maxLength={2}
                  placeholder="US"
                  value={merchantExtra.countryCode}
                  onChange={(e) => setMerchantExtra({ ...merchantExtra, countryCode: e.target.value })}
                />
              </Field>
            </div>
            <Field id="supportPhone" label="Support phone" error={errors.supportPhone}>
              <Input
                id="supportPhone"
                value={merchantExtra.supportPhone}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, supportPhone: e.target.value })}
              />
            </Field>
            <Field id="supportEmail" label="Support email" error={errors.supportEmail}>
              <Input
                id="supportEmail"
                type="email"
                value={merchantExtra.supportEmail}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, supportEmail: e.target.value })}
              />
            </Field>
            <Field id="refundPolicyUrl" label="Refund policy URL" error={errors.refundPolicyUrl}>
              <Input
                id="refundPolicyUrl"
                type="url"
                placeholder="https://"
                value={merchantExtra.refundPolicyUrl}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, refundPolicyUrl: e.target.value })}
              />
            </Field>
            <Field id="termsUrl" label="Terms URL" error={errors.termsUrl}>
              <Input
                id="termsUrl"
                type="url"
                placeholder="https://"
                value={merchantExtra.termsUrl}
                onChange={(e) => setMerchantExtra({ ...merchantExtra, termsUrl: e.target.value })}
              />
            </Field>
          </div>
        </fieldset>
      ) : null}

      {step === 2 ? (
        <fieldset className="space-y-4" data-testid="step-mid">
          <legend className="text-base font-semibold">MID details</legend>
          <Field id="midNumber" label="MID number" error={errors.midNumber}>
            <Input id="midNumber" value={mid.midNumber} onChange={(e) => setMid({ ...mid, midNumber: e.target.value })} />
          </Field>
          <Field id="mcc" label="MCC (4-digit code)" error={errors.mcc}>
            <Input id="mcc" inputMode="numeric" value={mid.mcc} onChange={(e) => setMid({ ...mid, mcc: e.target.value })} />
          </Field>
          <Field id="acquirerBin" label="Acquirer BIN (6 digits, starts with 4)" error={errors.acquirerBin}>
            <Input
              id="acquirerBin"
              inputMode="numeric"
              value={mid.acquirerBin}
              onChange={(e) => setMid({ ...mid, acquirerBin: e.target.value })}
            />
          </Field>
          <Field id="caid" label="Card Acceptor ID (CAID)" error={errors.caid}>
            <Input id="caid" value={mid.caid} onChange={(e) => setMid({ ...mid, caid: e.target.value })} />
          </Field>
          <Field id="descriptor" label="Descriptor (bank-statement text)" error={errors.descriptor}>
            <Input
              id="descriptor"
              maxLength={255}
              value={mid.descriptor}
              onChange={(e) => setMid({ ...mid, descriptor: e.target.value })}
            />
          </Field>

          <div className="space-y-4 border-t pt-4">
            <p className="text-sm font-medium text-fg-muted">Additional details (optional)</p>
            <Field
              id="descriptorVariations"
              label="Descriptor variations (one per line)"
              error={errors.descriptorVariations}
            >
              <Textarea
                id="descriptorVariations"
                rows={3}
                placeholder="ACME WIDGETS&#10;ACME*WIDGETS"
                value={midExtra.descriptorVariations}
                onChange={(e) => setMidExtra({ ...midExtra, descriptorVariations: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field id="bankMid" label="Bank MID" error={errors.bankMid}>
                <Input
                  id="bankMid"
                  value={midExtra.bankMid}
                  onChange={(e) => setMidExtra({ ...midExtra, bankMid: e.target.value })}
                />
              </Field>
              <Field id="bankTid" label="Bank TID" error={errors.bankTid}>
                <Input
                  id="bankTid"
                  value={midExtra.bankTid}
                  onChange={(e) => setMidExtra({ ...midExtra, bankTid: e.target.value })}
                />
              </Field>
              <Field id="alertMinimumAmount" label="Alert minimum amount" error={errors.alertMinimumAmount}>
                <Input
                  id="alertMinimumAmount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={midExtra.alertMinimumAmount}
                  onChange={(e) => setMidExtra({ ...midExtra, alertMinimumAmount: e.target.value })}
                />
              </Field>
              <Field id="alertMaximumAmount" label="Alert maximum amount" error={errors.alertMaximumAmount}>
                <Input
                  id="alertMaximumAmount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={midExtra.alertMaximumAmount}
                  onChange={(e) => setMidExtra({ ...midExtra, alertMaximumAmount: e.target.value })}
                />
              </Field>
            </div>
            <Field
              id="ethocaAmountThreshold"
              label="Ethoca minimum alert amount"
              error={errors.ethocaAmountThreshold}
            >
              <Input
                id="ethocaAmountThreshold"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={midExtra.ethocaAmountThreshold}
                onChange={(e) => setMidExtra({ ...midExtra, ethocaAmountThreshold: e.target.value })}
              />
            </Field>
          </div>
        </fieldset>
      ) : null}

      {step === 3 ? (
        <fieldset className="space-y-3" data-testid="step-services">
          <legend className="text-base font-semibold">Select services</legend>
          {errors.networks ? (
            <p className="text-sm text-destructive" role="alert">
              {errors.networks}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {ENROLLMENT_NETWORKS.map((n) => (
              <label
                key={n}
                className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${
                  networks.has(n) ? 'border-primary bg-primary/5' : ''
                }`}
                data-testid={`service-${n}`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input"
                  checked={networks.has(n)}
                  onChange={() => toggleNetwork(n)}
                  aria-label={NETWORK_LABELS[n]}
                />
                {NETWORK_LABELS[n]}
              </label>
            ))}
          </div>

          {networks.has('rdr') ? (
            <div className="space-y-4 rounded-md border p-4" data-testid="rdr-rule">
              <div>
                <p className="text-sm font-medium">RDR rule (Rapid Dispute Resolution)</p>
                <p className="text-xs text-fg-muted">
                  Defaults to the “Refund All” rule. Adjust the threshold below if you only want to
                  auto-refund above a certain amount.
                </p>
              </div>
              <Field id="rdrName" label="Rule name" error={errors['rdr.name']}>
                <Input
                  id="rdrName"
                  value={rdrRule.name}
                  onChange={(e) => setRdrRule({ ...rdrRule, name: e.target.value })}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field id="rdrAttribute" label="Attribute" error={errors['rdr.attribute']}>
                  <Input
                    id="rdrAttribute"
                    value={rdrRule.attribute}
                    onChange={(e) => setRdrRule({ ...rdrRule, attribute: e.target.value })}
                  />
                </Field>
                <Field id="rdrOperator" label="Operator" error={errors['rdr.operator']}>
                  <Input
                    id="rdrOperator"
                    value={rdrRule.operator}
                    onChange={(e) => setRdrRule({ ...rdrRule, operator: e.target.value })}
                  />
                </Field>
                <Field id="rdrValue" label="Value (amount)" error={errors['rdr.value']}>
                  <Input
                    id="rdrValue"
                    inputMode="decimal"
                    value={rdrRule.value}
                    onChange={(e) => setRdrRule({ ...rdrRule, value: e.target.value })}
                  />
                </Field>
              </div>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {step === 4 ? (
        <section className="space-y-4" data-testid="step-review">
          <h2 className="text-base font-semibold">Review &amp; submit</h2>
          <ReviewBlock title="Merchant" rows={merchantReviewRows} />
          <ReviewBlock title="MID" rows={midReviewRows} />
          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">Services</div>
            <div className="mt-1 text-fg-muted">
              {selectedNetworks.map((n) => NETWORK_LABELS[n]).join(', ')}
            </div>
          </div>
          {networks.has('rdr') ? (
            <ReviewBlock
              title="RDR rule"
              rows={[
                ['name', rdrRule.name],
                ['attribute', rdrRule.attribute],
                ['operator', rdrRule.operator],
                ['value', rdrRule.value],
              ]}
            />
          ) : null}
        </section>
      ) : null}

      {submitError ? (
        <p className="text-sm text-destructive" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className="flex items-center justify-between pt-2">
        <Button type="button" variant="outline" onClick={back} disabled={step === 0 || submitting}>
          Back
        </Button>
        {step < 4 ? (
          <Button type="button" onClick={next}>
            Next
          </Button>
        ) : (
          <Button type="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit enrollment'}
          </Button>
        )}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label="Progress">
      {STEP_LABELS.map((label, i) => (
        <li key={label} className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${
              i <= current ? 'border-primary bg-primary text-primary-foreground' : 'text-fg-muted'
            }`}
            aria-current={i === current ? 'step' : undefined}
          >
            {i + 1}
          </span>
          <span className={i === current ? 'font-medium' : 'text-fg-muted'}>{label}</span>
          {i < STEP_LABELS.length - 1 ? <span className="text-fg-muted">›</span> : null}
        </li>
      ))}
    </ol>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive" role="alert" data-testid={`error-${id}`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ReviewBlock({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <div className="rounded-md border p-3 text-sm">
      <div className="font-medium">{title}</div>
      <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2">
            <dt className="text-fg-muted">{k}</dt>
            <dd className="truncate">{v || '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
